import { Badge, Button, Checkbox, Group, Popover, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconFilter } from '@tabler/icons-react';

/**
 * Sentinel used as the checkbox value for the "No provider" bucket — sent to the
 * backend `providers` filter to match `provider_abbreviation IS NULL`. MUST match
 * the server's `NO_PROVIDER` token.
 */
const NO_PROVIDER = '__none__';

/** Abbreviation → display label. Falls back to the raw abbreviation if unknown. */
const PROVIDER_LABELS: Record<string, string> = {
    SB: 'SafeBase',
    MANUAL: 'Manual',
    AN: 'Anecdotes',
    CV: 'Conveyor',
    DR: 'Drata',
    HC: 'HyperComply',
    SC: 'Scrut',
    SF: 'SecureFrame',
    SK: 'Skypher',
    TC: 'TrustCloud',
    VT: 'Vanta',
    WH: 'Whistic',
};

const providerLabel = (value: string): string => PROVIDER_LABELS[value] ?? value;

interface ProviderFilterProps {
    /** Currently-selected provider abbreviations (may include the no-provider sentinel). */
    value: string[];
    /** Fired with the new selection whenever a checkbox toggles or Clear is pressed. */
    onChange: (providers: string[]) => void;
    /** Distinct provider abbreviations actually present in the data — the checkboxes to show. */
    options: string[];
    /** When true, render a "No provider" checkbox for the null-provider bucket. */
    hasNoProvider: boolean;
}

/**
 * Multi-checkbox provider filter. The checkboxes are the DISTINCT providers
 * present in the data (not the full static enum), plus a "No provider" option
 * when null-provider rows exist. The selection drives a SERVER-SIDE `providers`
 * filter. Shows a count badge when active and a Clear action to reset.
 */
export function ProviderFilter({
    value,
    onChange,
    options,
    hasNoProvider,
}: ProviderFilterProps): React.JSX.Element {
    const count = value.length;
    const isEmpty = options.length === 0 && !hasNoProvider;

    return (
        <Popover position="bottom-start" shadow="md" withinPortal>
            <Popover.Target>
                <Button
                    variant="default"
                    leftSection={<IconFilter size={16} stroke={1.5} />}
                    rightSection={<IconChevronDown size={16} stroke={1.5} />}
                    data-testid="ProviderFilterButton"
                >
                    <Group gap="xs" wrap="nowrap">
                        <Text size="sm">Provider</Text>
                        {count > 0 && (
                            <Badge size="sm" variant="filled" circle>
                                {count}
                            </Badge>
                        )}
                    </Group>
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="xs">
                    <Group justify="space-between" gap="xl">
                        <Text fw={600} size="sm">
                            Filter by provider
                        </Text>
                        {count > 0 && (
                            <Button
                                variant="subtle"
                                size="compact-xs"
                                onClick={() => {
                                    onChange([]);
                                }}
                                data-testid="ProviderFilterClear"
                            >
                                Clear
                            </Button>
                        )}
                    </Group>
                    {isEmpty ? (
                        <Text size="sm" c="dimmed">
                            No providers found.
                        </Text>
                    ) : (
                        <Checkbox.Group value={value} onChange={onChange}>
                            <Stack gap={6}>
                                {options.map((option) => (
                                    <Checkbox
                                        key={option}
                                        value={option}
                                        label={providerLabel(option)}
                                        data-testid={`ProviderFilterOption-${option}`}
                                    />
                                ))}
                                {hasNoProvider && (
                                    <Checkbox
                                        value={NO_PROVIDER}
                                        label="No provider"
                                        data-testid="ProviderFilterOption-none"
                                    />
                                )}
                            </Stack>
                        </Checkbox.Group>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}
